import { useRef } from 'react';
import { Admin } from './components/Admin';
import { Hero } from './components/Hero';
import { Demands } from './components/Demands';
import { News } from './components/News';
import { Steps } from './components/Steps';
import { SpreadWord } from './components/SpreadWord';
import { SubmitForm } from './components/SubmitForm';
import { Wall } from './components/Wall';
import { WallTeaser } from './components/WallTeaser';
import { SiteFooter } from './components/SiteFooter';

/**
 * There are exactly three pages and no in-app navigation between them, so a
 * path check beats adding a router. CloudFront already maps 403/404 to
 * /index.html with a 200 (scripts/setup.sh, CustomErrorResponses) and Vite's
 * dev server does SPA fallback by default, so /admin and /wall both serve the
 * shell with no config change.
 */
export function App() {
  const path = window.location.pathname.replace(/\/+$/, '');
  if (path === '/admin') return <Admin />;
  if (path === '/wall') return <WallPage />;
  return <PublicSite />;
}

/**
 * The full paginated wall, moved off the homepage: most visitors never scroll
 * to it, so this page is the opt-in path for anyone who wants to see more than
 * the homepage teaser (WallTeaser). No Hero here -- a slim header just links
 * back to the campaign page.
 */
function WallPage() {
  return (
    <>
      <header className="wall-page__header">
        <a className="wall-page__back" href="/">
          Back to the campaign
        </a>
      </header>

      <main>
        <Wall />
      </main>

      <SiteFooter />
    </>
  );
}

function PublicSite() {
  const stepsRef = useRef<HTMLElement>(null);

  const scrollTo = (ref: React.RefObject<HTMLElement | null>) => () =>
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <>
      <a className="skip-link" href="#demands">
        Skip to the demands
      </a>

      <Hero onHowTo={scrollTo(stepsRef)} />

      <main>
        <Demands />
        {/* News sits between the demands and the how-to by campaign direction
            (2026-09-21): a reader who has just read the ICE demand gets the
            coverage that backs it before being asked to act. Public site only,
            not the wall or admin pages. */}
        <News />
        <Steps ref={stepsRef} />
        <SubmitForm />
        <SpreadWord />
        <WallTeaser />
      </main>

      <SiteFooter />
    </>
  );
}
